import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTree } from '@fluidframework/react/alpha';
import { Add, Check, Delete, DragIndicator, Edit } from '@mui/icons-material';
import { Box, IconButton, List, ListSubheader } from '@mui/material';
import React from 'react';
export function EditableListView({ title, list, view, insertionForm, getKeyForItem, canEdit = true, canRemove = true, canReorder = true, }) {
    useTree(list);
    const [insertMode, setInsertMode] = React.useState(false);
    const [dragging, setDragging] = React.useState(undefined);
    const [dragOver, setDragOver] = React.useState(undefined);
    const reorder = React.useCallback((from, to) => {
        if (from === to) {
            return;
        }
        const indexFrom = list.indexOf(from);
        let indexTo = list.indexOf(to);
        if (indexFrom < indexTo) {
            indexTo++;
        }
        list.moveToIndex(indexTo, indexFrom);
    }, [list]);
    const handleDragStart = React.useCallback((item, ev) => {
        if (!canReorder) {
            return;
        }
        setDragging(item);
        ev.dataTransfer.effectAllowed = 'move';
        ev.dataTransfer.setData('text/plain', list.indexOf(item).toString());
    }, [list, canReorder]);
    const handleDragEnter = (item, _ev) => {
        if (!canReorder || dragging === undefined) {
            return;
        }
        if (item !== dragging) {
            setDragOver(item);
        }
    };
    const handleDragOver = (item, ev) => {
        if (!canReorder)
            return;
        ev.preventDefault();
        if (dragging !== undefined && item !== dragging) {
            setDragOver(item);
        }
    };
    const handleDrop = (item, ev) => {
        if (!canReorder || dragging === undefined)
            return;
        ev.preventDefault();
        reorder(dragging, item);
        setDragging(undefined);
        setDragOver(undefined);
    };
    const handleDragEnd = () => {
        setDragging(undefined);
        setDragOver(undefined);
    };
    return (_jsxs(Box, { children: [_jsx(List, { subheader: title && (_jsx(ListSubheader, { component: "div", id: "nested-list-subheader", children: title })), children: list.map((item, index) => {
                    const key = getKeyForItem(item);
                    const isDragging = index === dragging;
                    const isDragOver = index === dragOver && dragging !== undefined && dragging !== index;
                    return (_jsx(EditableListEntry, { item: item, view: view, canEdit: canEdit, removeItem: canRemove
                            ? () => {
                                list.removeAt(index);
                            }
                            : undefined, canReorder: canReorder, isDragging: isDragging, isDragOver: isDragOver, onDragStart: (ev) => handleDragStart(item, ev), onDragEnter: (ev) => handleDragEnter(item, ev), onDragOver: (ev) => handleDragOver(item, ev), onDrop: (ev) => handleDrop(item, ev), onDragEnd: handleDragEnd }, key));
                }) }), !insertMode && insertionForm && (_jsx(IconButton, { onClick: () => {
                    setInsertMode(true);
                }, children: _jsx(Add, {}) })), insertMode && insertionForm && (_jsx(Box, { children: insertionForm(list, () => {
                    setInsertMode(false);
                }) }))] }));
}
export function EditableListEntry({ item, key, view, removeItem, canEdit = true, canReorder = true, isDragging = false, isDragOver = false, onDragStart, onDragEnter, onDragOver, onDrop, onDragEnd, }) {
    const [editMode, setEditMode] = React.useState(false);
    const innerView = view(item, editMode);
    if (editMode) {
        return (_jsxs(Box, { display: 'flex', flexDirection: "row", width: "100%", justifyContent: "space-between", draggable: canReorder, onDragStart: onDragStart, onDragEnter: onDragEnter, onDragOver: onDragOver, onDrop: onDrop, onDragEnd: onDragEnd, sx: {
                border: isDragOver ? '1px dashed #999' : '1px solid transparent',
                opacity: isDragging ? 0.5 : 1,
                transition: 'border 0.15s ease',
            }, children: [canReorder && (_jsx(IconButton, { size: "small", sx: { cursor: 'grab', mr: 1 }, "aria-label": "drag-handle", children: _jsx(DragIndicator, { fontSize: "small" }) })), innerView, _jsx(IconButton, { onClick: () => setEditMode(false), children: _jsx(Check, {}) })] }, key));
    }
    else {
        return (_jsxs(Box, { display: 'flex', flexDirection: "row", width: "100%", justifyContent: "space-between", draggable: canReorder, onDragStart: onDragStart, onDragEnter: onDragEnter, onDragOver: onDragOver, onDrop: onDrop, onDragEnd: onDragEnd, sx: {
                border: isDragOver ? '1px dashed #999' : '1px solid transparent',
                opacity: isDragging ? 0.5 : 1,
                transition: 'border 0.15s ease',
            }, children: [canReorder && (_jsx(IconButton, { size: "small", disableRipple: true, disableFocusRipple: true, sx: {
                        cursor: 'grab',
                        mr: 1,
                        backgroundColor: 'transparent',
                        '&:hover': { backgroundColor: 'transparent' },
                    }, "aria-label": "drag-handle", children: _jsx(DragIndicator, { fontSize: "small" }) })), innerView, canEdit && (_jsx(IconButton, { onClick: () => setEditMode(true), children: _jsx(Edit, {}) })), removeItem && (_jsx(IconButton, { onClick: removeItem, children: _jsx(Delete, {}) }))] }, key));
    }
}
//# sourceMappingURL=EditableListView.js.map